'use client';

import { useRouter } from 'next/navigation';
import { createContext, type ReactNode, useContext, useEffect, useRef } from 'react';
import { carregarContextos } from '@/features/contextoAcesso/store';
import { rotaDoContexto } from '@/features/contextoAcesso/utils/rotaDoContexto';
import { deveConfinarAoPortal } from '@/shared/utils/sessaoPortal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { AuthUser, LoginCredentials, RequestTokenResponse, VerifyTokenCredentials } from '@/types/auth/authTypes';
import { loadAuthSession, logout as logoutAction, requestToken as requestTokenAction, updateUserProfile as updateUserProfileAction, verifyToken as verifyTokenAction } from '../store';
import { selectAuthState } from '../store/selectors';
function getSafeRedirectPath(redirectTo?: string) {
  if (!redirectTo) return '/';
  const value = redirectTo.trim();
  if (!value.startsWith('/')) return '/';
  if (value.startsWith('//')) return '/';
  if (value.includes('\\')) return '/';
  return value;
}
interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requestToken: (credentials: LoginCredentials) => Promise<RequestTokenResponse>;
  verifyToken: (credentials: VerifyTokenCredentials, redirectTo?: string) => Promise<{
    needsProfileCompletion: boolean;
  }>;
  completeProfile: (nomeCompleto: string) => Promise<void>;
  logout: () => Promise<void>;
}
const AuthContext = createContext<AuthContextValue | undefined>(undefined);
interface AuthProviderProps {
  children: ReactNode;
}
export function AuthProvider({
  children
}: AuthProviderProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    isLoginInProgress
  } = useAppSelector(selectAuthState);
  const hasLoadedAuth = useRef(false);
  const pendingRedirectRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isLoginInProgress && !hasLoadedAuth.current) {
      hasLoadedAuth.current = true;
      dispatch(loadAuthSession());
    }
  }, [dispatch, isLoginInProgress]);
  const requestToken = async (credentials: LoginCredentials) => {
    return await dispatch(requestTokenAction(credentials)).unwrap();
  };

  /**
   * Ponto de entrada pelo ULTIMO contexto acessado (decisao de produto): quem tem
   * os dois papeis cai onde estava, sem tela intermediaria.
   *
   * Devolve `null` — e o login segue pela rota default — em tres casos:
   *   1. a conta nao tem contexto memorizado;
   *   2. o ultimo contexto e WORKSPACE (a home ja resolve);
   *   3. a SESSAO nao e de portal. Uma conta de plataforma com contexto de parte
   *      memorizado seria mandada para `/portal/...`, que o layout do portal
   *      recusa por nao ter `portalScope` — e o redirect de volta para `/login`
   *      fecharia um laco que so sairia limpando a coluna no banco.
   *
   * Best-effort: falhar ao consultar nao pode travar o login.
   */
  const resolverDestinoPorUltimoContexto = async (user_0: AuthUser): Promise<string | null> => {
    if (!deveConfinarAoPortal(user_0)) return null;
    const resultado = await dispatch(carregarContextos());
    if (!carregarContextos.fulfilled.match(resultado)) return null;
    const ultimo = resultado.payload.ultimoContexto;
    if (!ultimo || ultimo.tipo === 'WORKSPACE') return null;
    return rotaDoContexto(ultimo);
  };
  const verifyToken = async (credentials_0: VerifyTokenCredentials, redirectTo?: string): Promise<{
    needsProfileCompletion: boolean;
  }> => {
    try {
      const response = await dispatch(verifyTokenAction(credentials_0)).unwrap();
      const user_1 = response.user;
      const needsProfileCompletion = !user_1.nomeCompleto || user_1.nomeCompleto.trim() === '';
      if (needsProfileCompletion) {
        pendingRedirectRef.current = response.redirectTo || redirectTo || null;
        return {
          needsProfileCompletion: true
        };
      }
      if (deveConfinarAoPortal(user_1)) {
        sessionStorage.setItem('portal_fresh_login', '1');
        const deepLink = getSafeRedirectPath(redirectTo);
        // O deep link que originou o login ganha do contexto memorizado: quem
        // clicou no link de uma notificacao nova precisa cair NELA, nao no ultimo
        // processo que abriu.
        const destino = deepLink !== '/' ? deepLink : (await resolverDestinoPorUltimoContexto(user_1)) ?? '/portal/dashboard';
        router.replace(destino);
        return {
          needsProfileCompletion: false
        };
      }
      if (user_1.tipoUsuario === 'ARBITRO') {
        const arbitroRedirect = getSafeRedirectPath(redirectTo) === '/' ? '/arbitro' : getSafeRedirectPath(redirectTo);
        router.replace(arbitroRedirect);
        return {
          needsProfileCompletion: false
        };
      }
      if (user_1.tipoUsuario === 'ADVOGADO' && user_1.portalAdvogadoAtivo) {
        const advogadoRedirect = getSafeRedirectPath(redirectTo) === '/' ? '/advogado' : getSafeRedirectPath(redirectTo);
        router.replace(advogadoRedirect);
        return {
          needsProfileCompletion: false
        };
      }
      const finalRedirect = response.redirectTo || redirectTo;
      const safeRedirectTo = getSafeRedirectPath(finalRedirect);
      router.replace(safeRedirectTo);
      return {
        needsProfileCompletion: false
      };
    } catch (error_0) {
      throw error_0;
    }
  };
  const completeProfile = async (nomeCompleto: string) => {
    if (!user) throw new Error('Usu�rio n�o autenticado');
    await dispatch(updateUserProfileAction({
      userId: user.id,
      nomeCompleto
    })).unwrap();
    const redirectPath = getSafeRedirectPath(pendingRedirectRef.current ?? undefined);
    pendingRedirectRef.current = null;
    router.replace(redirectPath);
  };
  const logout = async () => {
    await dispatch(logoutAction(undefined)).unwrap();
    router.replace('/login');
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated,
    isLoading,
    error,
    requestToken,
    verifyToken,
    completeProfile,
    logout
  }}>
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
}